import React from 'react';

const IndividualScores = ({ responses, tredeNames }) => {
  const tredes = ['vertrouwen', 'conflicten', 'betrokkenheid', 'verantwoording', 'resultaten'];
  
  // Calculate spreiding (spread) analysis
  const calculateSpreiding = () => {
    const spreiding = {};
    
    tredes.forEach(trede => {
      const scores = responses
        .map(response => response.tredeScores?.[trede])
        .filter(score => score !== undefined);
      
      if (scores.length > 1) {
        const min = Math.min(...scores);
        const max = Math.max(...scores);
        const verschil = max - min;
        
        spreiding[trede] = {
          min: Math.round(min * 10) / 10,
          max: Math.round(max * 10) / 10,
          verschil: Math.round(verschil * 10) / 10,
          status: verschil < 1.0 ? 'goed' : verschil < 2.0 ? 'matig' : 'groot'
        };
      }
    });
    
    return spreiding;
  };

  const spreiding = calculateSpreiding();

  // Get status icon and color
  const getSpreadingStatus = (status) => {
    switch (status) {
      case 'goed': return { icon: '✅', color: '#22c55e', label: 'Kleine verschillen' };
      case 'matig': return { icon: '⚠️', color: '#f59e0b', label: 'Gemiddelde verschillen' };
      case 'groot': return { icon: '🚨', color: '#ef4444', label: 'Grote verschillen' };
      default: return { icon: '❓', color: '#6b7280', label: 'Onbekend' };
    }
  };

  if (responses.length === 0) {
    return (
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '15px',
        marginBottom: '2rem',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
        textAlign: 'center'
      }}>
        <h3 style={{ 
          fontSize: '1.5rem', 
          fontWeight: 'bold', 
          color: '#2d3748',
          marginBottom: '1rem'
        }}>
          👥 Individuele Scores
        </h3>
        <p style={{ color: '#6b7280' }}>
          Nog geen individuele scores beschikbaar.
        </p>
      </div>
    );
  }

  return (
    <div style={{
      background: 'white',
      padding: '2rem',
      borderRadius: '15px',
      marginBottom: '2rem',
      boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
    }}>
      <h3 style={{ 
        fontSize: '1.5rem', 
        fontWeight: 'bold', 
        color: '#2d3748',
        marginBottom: '2rem',
        textAlign: 'center'
      }}>
        👥 Individuele Scores
        </h3>

      {/* Spreiding Analyse */}
      <div style={{
        background: '#f8fafc',
        border: '2px solid #e2e8f0',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <h4 style={{
          fontSize: '1.125rem',
          fontWeight: 'bold',
          color: '#2d3748',
          marginBottom: '1rem'
        }}>
          📊 Spreiding Analyse
        </h4>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem'
        }}>
          {tredes.map(trede => {
            const spread = spreiding[trede];
            if (!spread) return null;
            
            const statusInfo = getSpreadingStatus(spread.status);
            
            return (
              <div key={trede} style={{
                background: 'white',
                padding: '1rem',
                borderRadius: '8px',
                border: `2px solid ${statusInfo.color}20`,
                borderLeft: `4px solid ${statusInfo.color}`
              }}>
                <div style={{
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  {tredeNames[trede]}
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '0.25rem'
                }}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    {spread.min} - {spread.max}
                  </span>
                  <span style={{
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    color: statusInfo.color
                  }}>
                    {statusInfo.icon} {spread.verschil}
                  </span>
                </div>
                <div style={{
                  fontSize: '0.75rem',
                  color: statusInfo.color,
                  fontWeight: '500'
                }}>
                  {statusInfo.label}
                </div>
              </div>
            );
          })}
        </div>
        
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          background: '#dbeafe',
          borderRadius: '8px',
          fontSize: '0.875rem',
          color: '#1e40af',
          lineHeight: '1.6'
        }}>
          <strong>💡 Spreiding Interpretatie:</strong>
          <div style={{ marginTop: '0.5rem' }}>
            • <strong>Verschil &lt; 1.0 punt:</strong> Eensgezind (acceptabele variatie)<br/>
            • <strong>Verschil 1.0-1.9 punten:</strong> Aandacht nodig (verschillende beleving)<br/>
            • <strong>Verschil ≥ 2.0 punten:</strong> Prioriteit (sterk uiteenlopende realiteiten)
          </div>
        </div>
        </div>


      {/* Individuele Scores Tabel */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.875rem'
        }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{
                padding: '1rem 0.75rem',
                textAlign: 'left',
                fontWeight: '600',
                color: '#374151',
                borderBottom: '2px solid #e5e7eb'
              }}>
                Deelnemer
              </th>
              {tredes.map(trede => (
                <th key={trede} style={{
                  padding: '1rem 0.75rem',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#374151',
                  borderBottom: '2px solid #e5e7eb'
                }}>
                  {tredeNames[trede]}
                </th>
              ))}
              <th style={{
                padding: '1rem 0.75rem',
                textAlign: 'center',
                fontWeight: '600',
                color: '#374151',
                borderBottom: '2px solid #e5e7eb'
              }}>
                Gemiddelde
              </th>
            </tr>
          </thead>
          <tbody>
            {responses.map((response, index) => {
              // Calculate individual average
              const scores = tredes
                .map(trede => response.tredeScores?.[trede])
                .filter(score => score !== undefined);
              const average = scores.length > 0 
                ? scores.reduce((sum, score) => sum + score, 0) / scores.length
                : 0;

              return (
                <tr key={response.id} style={{
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <td style={{
                    padding: '1rem 0.75rem',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                  {response.participantName || response.participantEmail || `Deelnemer ${index + 1}`}
                  </td>
                  {tredes.map(trede => {
                    const score = response.tredeScores?.[trede];
                    let bgColor = '#f9fafb';
                    let textColor = '#6b7280';
                    
                    if (score !== undefined) {
                      if (score >= 4) {
                        bgColor = '#dcfce7';
                        textColor = '#166534';
                      } else if (score >= 3.5) {
                        bgColor = '#fef3c7';
                        textColor = '#92400e';
                      } else {
                        bgColor = '#fee2e2';
                        textColor = '#dc2626';
                      }
                    }
                    
                    return (
                      <td key={trede} style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'center',
                        background: bgColor,
                        color: textColor,
                        fontWeight: '600'
                      }}>
                        {score !== undefined ? score.toFixed(1) : '-'}
                      </td>
                    );
                  })}
                  <td style={{
                    padding: '1rem 0.75rem',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    color: average >= 3.5 ? '#059669' : '#dc2626',
                    background: average >= 3.5 ? '#ecfdf5' : '#fef2f2'
                  }}>
                    {average > 0 ? average.toFixed(1) : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary Stats */}
      <div style={{
        marginTop: '2rem',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem'
      }}>
        <div style={{
          background: '#f0fdf4',
          border: '2px solid #22c55e',
          borderRadius: '8px',
          padding: '1rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#22c55e' }}>
            {responses.length}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#166534' }}>
            Totaal deelnemers
          </div>
        </div>
        
        <div style={{
          background: '#eff6ff',
          border: '2px solid #3b82f6',
          borderRadius: '8px',
          padding: '1rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3b82f6' }}>
            {Object.values(spreiding).filter(s => s.status === 'goed').length}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#1e40af' }}>
            Eensgezind
          </div>
        </div>
        
        <div style={{
          background: '#fef2f2',
          border: '2px solid #ef4444',
          borderRadius: '8px',
          padding: '1rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ef4444' }}>
            {Object.values(spreiding).filter(s => s.status === 'groot').length}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#dc2626' }}>
            Prioriteit
          </div>
        </div>
      </div>

      {/* Privacy Notice */}
      <div style={{
        marginTop: '2rem',
        padding: '1rem',
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '0.75rem',
        color: '#6b7280',
        textAlign: 'center'
      }}>
        Voor therapeutische doeleinden zijn de namen zichtbaar om deelnemers te kunnen identificeren.
      </div>
    </div>
  );
};

export default IndividualScores;